"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Compass, Moon, Star, Sparkles, BookOpen, Telescope } from 'lucide-react';
import { AboutSidebar } from '@/components/about/sidebar';

export default function DraconicPage() {
  return (
    <div className="min-h-screen bg-slate-950">
      <div className="flex h-screen">
        <AboutSidebar activeSection="astrology" />
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-4 py-8">
            <div className="mb-4 p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
              <div className="flex items-center gap-2 text-purple-300">
                <Telescope className="h-4 w-4" />
                <span className="text-sm font-medium">🔭 Astroloji</span>
                <span className="text-purple-500">/</span>
                <span className="text-sm">Gelişmiş Sistemler</span>
                <span className="text-purple-500">/</span>
                <span className="text-sm">Draconic Harita</span>
              </div>
            </div>

            <div className="mb-6">
              <Link href="/about" className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors">
                <ArrowLeft className="h-4 w-4 mr-2" />
                AXTROLOG Nedir
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link href="/about/astrology" className="text-slate-400 hover:text-slate-200 transition-colors">
                Astroloji
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link href="/about/astrology/advanced-systems" className="text-slate-400 hover:text-slate-200 transition-colors">
                Gelişmiş Sistemler
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <span className="text-slate-300">Draconic Harita</span>
            </div>

            <div className="bg-gradient-to-r from-purple-900/20 to-indigo-900/20 rounded-xl p-8 mb-8 border border-purple-500/20">
              <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
                <Compass className="h-10 w-10 text-purple-400" />
                Draconic Harita
              </h1>
              <p className="text-lg text-slate-200 leading-relaxed">
                Kuzey Lunar Node'u 0° Koç olarak alıp hesaplanan ruhsal harita. Kişinin karma misyonu,
                ruh seviyesindeki potansiyeli ve geçmiş yaşamlardan getirdiği yetenekleri gösterir.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Hesaplama Yöntemi</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <Moon className="h-5 w-5 text-purple-400" />
                      Temel Formül
                    </h3>
                    <div className="space-y-3">
                      <p className="text-slate-300">
                        Draconic Pozisyon = Natal Pozisyon - Kuzey Node Pozisyonu
                      </p>
                      <div className="bg-purple-900/20 border border-purple-700/50 rounded-lg p-4">
                        <h4 className="font-semibold text-purple-200 mb-2">Örnek Hesaplama:</h4>
                        <div className="space-y-1 text-sm text-slate-300">
                          <div>Natal Güneş: 15° Aslan (135°)</div>
                          <div>Kuzey Node: 10° İkizler (70°)</div>
                          <div className="border-t border-purple-700/30 pt-2 mt-2">
                            <strong>Draconic Güneş: 135° - 70° = 65° = 5° İkizler</strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-indigo-400" />
                      Ruhsal Referans Sistemi
                    </h3>
                    <div className="space-y-3">
                      <p className="text-slate-300">
                        Kuzey Node'u 0° Koç yaparak, ruhun "doğal" pozisyonları
                        ve karma görevleri açığa çıkar.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-indigo-400 rounded-full"></div>
                          <span className="text-sm text-slate-400">Ruhun doğal eğilimleri</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-indigo-400 rounded-full"></div>
                          <span className="text-sm text-slate-400">Karma misyon ve amaç</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-indigo-400 rounded-full"></div>
                          <span className="text-sm text-slate-400">Geçmiş yaşam yetenekleri</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Draconic vs Natal</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-xl font-semibold text-slate-100 mb-4 text-blue-300">Natal Harita</h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">Kişiliğin dünyasal ifadesi ve bu yaşamdaki deneyimleri</p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400">Çevresel koşulların etkisi</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400">Sosyal rollar ve kimlik</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400">Günlük yaşam deneyimleri</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-xl font-semibold text-slate-100 mb-4 text-purple-300">Draconic Harita</h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">Ruhun saf hali ve karma seviyesindeki potansiyel</p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400">Ruhsal gelişim seviyesi</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400">Doğal yetenekler ve güçlü yönler</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400">Karma misyon ve amaç</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Draconic Gezegenlerin Anlamları</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 text-yellow-300">Kişisel Gezegenler</h3>
                  <div className="space-y-3">
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☉ Draconic Güneş</div>
                      <div className="text-sm text-slate-400">Ruhun gerçek kimliği, karma misyonun özü</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☽ Draconic Ay</div>
                      <div className="text-sm text-slate-400">Ruhsal ihtiyaçlar, geçmiş yaşam kalıpları</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☿ Draconic Merkür</div>
                      <div className="text-sm text-slate-400">Ruhsal iletişim tarzı, öğrenme şekli</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♀ Draconic Venüs</div>
                      <div className="text-sm text-slate-400">Ruhsal sevgi dili, değerler sistemi</div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 text-green-300">Sosyal ve Ruhsal Gezegenler</h3>
                  <div className="space-y-3">
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♂ Draconic Mars</div>
                      <div className="text-sm text-slate-400">Ruhsal motivasyon, karma eylemler</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♃ Draconic Jüpiter</div>
                      <div className="text-sm text-slate-400">Ruhsal bilgelik, genişleme alanları</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♄ Draconic Satürn</div>
                      <div className="text-sm text-slate-400">Karma dersler, ruhsal disiplin</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♅♆♇ Draconic Uranüs/Neptün/Pluto</div>
                      <div className="text-sm text-slate-400">Generasyonel karma, kollektif misyon</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Önerilen Kaynaklar
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-slate-200 mb-3">Temel Eserler</h4>
                  <div className="space-y-2 text-sm text-slate-400">
                    <div>• "The Draconic Chart" - Pamela Crane</div>
                    <div>• "Karmic Astrology" - Martin Schulman</div>
                    <div>• "Soul-Centered Astrology" - Alan Oken</div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-200 mb-3">İleri Çalışmalar</h4>
                  <div className="space-y-2 text-sm text-slate-400">
                    <div>• "Astrology and the Soul" - Jan Spiller</div>
                    <div>• "Evolutionary Astrology" - Steven Forrest</div>
                    <div>• "The Lunar Nodes" - Celeste Teal</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-red-200 mb-3">⚠️ Önemli Açıklama</h3>
              <p className="text-red-100 leading-relaxed">
                Draconic Harita ruhsal ve karma konularla ilgili spekülatif bir tekniktir.
                Bu bilgiler sadece eğitim ve farkındalık amaçlıdır, bilimsel kesinlik iddiası taşımaz.
                Kişisel gelişim için rehber olarak kullanılmalıdır.
              </p>
            </div>

            <div className="flex justify-between items-center">
              <Link
                href="/about/astrology/advanced-systems/harmonics"
                className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Harmonik Astroloji
              </Link>

              <Link
                href="/about/astrology/advanced-systems/heliocentric"
                className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
              >
                Heliocentric Astroloji
                <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}